'use client'

import React, { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Loader2, Save, AlertCircle, Eye, EyeOff, Camera, UserCog } from "lucide-react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { getMe } from "@/lib/api/me"
import { toast } from "sonner"
import * as configApi from "@/lib/api/config"
import { uploadFoto as uploadMeFoto, updateMe, changePassword } from "@/lib/api/me"
import { UserAvatar } from "@/components/layout/user-avatar"
import { FotoEditorDialog } from "./foto-editor-dialog"
import { crmStyles, ModalForm } from "@/components/shared/crm-ui"
import { cn } from "@/lib/utils"

const PRESET_COLORS = [
  { name: 'Azul',     value: '#3788d8' },
  { name: 'Verde',    value: '#10b981' },
  { name: 'Púrpura',  value: '#8b5cf6' },
  { name: 'Rosa',     value: '#ec4899' },
  { name: 'Naranja',  value: '#f59e0b' },
  { name: 'Rojo',     value: '#ef4444' },
  { name: 'Índigo',   value: '#6366f1' },
  { name: 'Turquesa', value: '#14b8a6' },
]

const MODULOS_GRUPOS = [
  {
    label: "General",
    items: [
      { key: "dashboard",    label: "Dashboard" },
      { key: "pacientes",    label: "Pacientes" },
      { key: "agenda",       label: "Agenda" },
      { key: "presupuestos", label: "Presupuestos" },
    ],
  },
  {
    label: "Clínica",
    items: [
      { key: "finanzas",     label: "Finanzas" },
      { key: "reportes",     label: "Reportes" },
      { key: "laboratorios", label: "Laboratorios" },
      { key: "insumos",      label: "Insumos" },
      { key: "catalogo",     label: "Catálogo" },
    ],
  },
  {
    label: "Otros",
    items: [
      { key: "marketing",     label: "Marketing" },
      { key: "configuracion", label: "Configuración" },
    ],
  },
]

const MODULOS_DEFAULT: Record<string, string[]> = {
  admin:       ["dashboard","pacientes","agenda","presupuestos","finanzas","reportes","laboratorios","insumos","catalogo","marketing","configuracion"],
  profesional: ["dashboard","pacientes","agenda","presupuestos","finanzas","reportes","laboratorios","insumos","catalogo"],
  recepcion:   ["dashboard","pacientes","agenda","presupuestos"],
  marketing:   ["dashboard","pacientes","insumos","catalogo","marketing"],
}

interface UsuarioModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave?: (data: configApi.UsuarioIn) => Promise<void>
  mode: 'create' | 'edit'
  initialData?: configApi.UsuarioOut
  /** true cuando el usuario edita su propio perfil desde el sidebar */
  selfEdit?: boolean
}

export function UsuarioModal({ open, onOpenChange, onSave, mode, initialData, selfEdit = false }: UsuarioModalProps) {
  const qc = useQueryClient()
  const { data: meData } = useQuery({ queryKey: ["me"], queryFn: getMe, staleTime: Infinity })
  const fileRef = useRef<HTMLInputElement>(null)

  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showPassword, setShowPassword] = useState(false)
  const [showCurrentPass, setShowCurrentPass] = useState(false)

  // Foto
  const [fotoSrc, setFotoSrc] = useState<string | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [fotoUrl, setFotoUrl] = useState("")
  const [uploadingFoto, setUploadingFoto] = useState(false)

  // Contraseña (selfEdit)
  const [currentPass, setCurrentPass] = useState("")
  const [newPass, setNewPass] = useState("")
  const [confirmPass, setConfirmPass] = useState("")

  // Módulos
  const [modulosCustom, setModulosCustom] = useState<string[] | null>(null)

  const [formData, setFormData] = useState<Partial<configApi.UsuarioIn>>({
    username: '', first_name: '', last_name: '', email: '',
    password: '', rol: 'recepcion', color_agenda: '#6366f1', activo: true,
  })

  useEffect(() => {
    if (!open) return
    setErrors({})
    setCurrentPass(""); setNewPass(""); setConfirmPass("")
    setFotoSrc(null)

    if (mode === 'edit' && initialData) {
      setFormData({
        username: initialData.username,
        first_name: initialData.first_name,
        last_name: initialData.last_name,
        email: initialData.email,
        password: '',
        rol: initialData.rol,
        color_agenda: initialData.color_agenda,
        activo: initialData.activo,
      })
      setFotoUrl(initialData.foto_url || "")
      setModulosCustom(initialData.modulos_habilitados ?? null)
    } else {
      setFormData({ username: '', first_name: '', last_name: '', email: '', password: '', rol: 'recepcion', color_agenda: '#6366f1', activo: true })
      setFotoUrl("")
      setModulosCustom(null)
    }
  }, [open, mode, initialData])

  function handleRolChange(rol: string) {
    setFormData(f => ({ ...f, rol }))
    // Solo actualizar módulos si ya estaban personalizados
    if (modulosCustom !== null) {
      setModulosCustom(MODULOS_DEFAULT[rol] ?? [])
    }
  }

  function toggleModulo(key: string) {
    const current = modulosCustom ?? MODULOS_DEFAULT[formData.rol ?? 'recepcion'] ?? []
    setModulosCustom(
      current.includes(key) ? current.filter(m => m !== key) : [...current, key]
    )
  }

  function getModulosActivos(): string[] {
    return modulosCustom ?? MODULOS_DEFAULT[formData.rol ?? 'recepcion'] ?? []
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => { setFotoSrc(reader.result as string); setEditorOpen(true) }
    reader.readAsDataURL(file)
    e.target.value = ""
  }

  async function handleFotoApply(blob: Blob) {
    setUploadingFoto(true)
    try {
      const file = new File([blob], "foto.jpg", { type: "image/jpeg" })
      if (selfEdit) {
        const result = await uploadMeFoto(file)
        setFotoUrl(result.foto_url)
        qc.setQueryData(["me"], (old: any) => old ? { ...old, foto_url: result.foto_url } : old)
        qc.invalidateQueries({ queryKey: ["me"] })
      } else if (initialData?.id) {
        const result = await configApi.uploadUsuarioFoto(initialData.id, file)
        setFotoUrl(result.foto_url)
        qc.invalidateQueries({ queryKey: ["usuarios"] })
        // Si el admin cambió la foto de su propio usuario, sincronizar el sidebar
        if (meData?.id === initialData.id) {
          qc.setQueryData(["me"], (old: any) => old ? { ...old, foto_url: result.foto_url } : old)
        }
      }
      toast.success("Foto actualizada")
    } catch {
      toast.error("No se pudo subir la foto")
    } finally {
      setUploadingFoto(false)
    }
  }

  function validateForm(): boolean {
    const newErrors: Record<string, string> = {}
    if (!selfEdit && mode === 'create') {
      if (!formData.username || formData.username.length < 3) newErrors.username = 'Mínimo 3 caracteres'
      if (!formData.password || formData.password.length < 8) newErrors.password = 'Mínimo 8 caracteres'
    }
    if (!formData.first_name?.trim()) newErrors.first_name = 'Requerido'
    if (!formData.last_name?.trim()) newErrors.last_name = 'Requerido'
    if (!formData.email?.includes('@')) newErrors.email = 'Email inválido'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  async function handleSubmit() {
    if (!validateForm()) return
    setSaving(true)
    try {
      if (selfEdit) {
        const updated = await updateMe(formData.first_name!, formData.last_name!, formData.email!)
        qc.setQueryData(["me"], (old: any) => old ? { ...old, nombre: updated.nombre, email: updated.email } : old)
        qc.invalidateQueries({ queryKey: ["me"] })
        if (newPass) {
          if (newPass !== confirmPass) { setErrors({ confirmPass: "Las contraseñas no coinciden" }); setSaving(false); return }
          if (newPass.length < 8) { setErrors({ newPass: "Mínimo 8 caracteres" }); setSaving(false); return }
          if (!currentPass) { setErrors({ currentPass: "Ingresá tu contraseña actual" }); setSaving(false); return }
          await changePassword(currentPass, newPass)
          setCurrentPass(""); setNewPass(""); setConfirmPass("")
          toast.success("Contraseña actualizada")
        }
        toast.success("Perfil actualizado")
        onOpenChange(false)
      } else {
        if (onSave) {
          await onSave({ ...formData as configApi.UsuarioIn, modulos_habilitados: modulosCustom })
          onOpenChange(false)
        }
      }
    } catch (error: any) {
      setErrors({ general: error.message })
    } finally {
      setSaving(false)
    }
  }

  const nombreCompleto = `${formData.first_name || ''} ${formData.last_name || ''}`.trim() || "Usuario"

  return (
    <>
      <ModalForm
        open={open}
        onClose={() => onOpenChange(false)}
        title={selfEdit ? 'Mi perfil' : mode === 'create' ? 'Nuevo Usuario' : 'Editar Usuario'}
        subtitle={selfEdit ? 'Actualizá tu información personal.' : mode === 'create' ? 'Completa los datos para el nuevo acceso.' : 'Actualizá la información y permisos.'}
        icon={<UserCog className="size-4 text-primary" />}
        maxWidth="max-w-lg"
        footer={
          <>
            <div />
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={saving}
                className="gap-2"
              >
                {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                {selfEdit ? 'Guardar cambios' : mode === 'create' ? 'Crear Usuario' : 'Guardar Cambios'}
              </Button>
            </div>
          </>
        }
      >
          <div className="space-y-5 py-2">

            {/* Avatar */}
            <div className="flex flex-col items-center gap-2">
              <div className="relative group cursor-pointer" onClick={() => fileRef.current?.click()}>
                <UserAvatar nombre={nombreCompleto} fotoUrl={fotoUrl} size="lg" />
                <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  {uploadingFoto
                    ? <Loader2 className="size-5 text-white animate-spin" />
                    : <Camera className="size-5 text-white" />
                  }
                </div>
              </div>
              <button type="button" onClick={() => fileRef.current?.click()} className="text-[11px] text-primary hover:underline disabled:opacity-50" disabled={uploadingFoto}>
                {uploadingFoto ? "Subiendo…" : "Cambiar foto"}
              </button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" aria-label="Seleccionar foto" onChange={handleFileChange} />
            </div>

            {/* Nombre y Apellido */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-muted-foreground/70 uppercase tracking-widest ml-1">Nombre *</label>
                <input value={formData.first_name} onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                  className={cn(crmStyles.input, errors.first_name && 'border-status-error')} placeholder="Nombre" />
                {errors.first_name && <p className="text-xs text-status-error ml-1">{errors.first_name}</p>}
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-muted-foreground/70 uppercase tracking-widest ml-1">Apellido *</label>
                <input value={formData.last_name} onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                  className={cn(crmStyles.input, errors.last_name && 'border-status-error')} placeholder="Apellido" />
                {errors.last_name && <p className="text-xs text-status-error ml-1">{errors.last_name}</p>}
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-muted-foreground/70 uppercase tracking-widest ml-1">Email *</label>
              <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                className={cn(crmStyles.input, errors.email && 'border-status-error')} placeholder="email@ejemplo.com" />
              {errors.email && <p className="text-xs text-status-error ml-1">{errors.email}</p>}
            </div>

            {/* Username — create admin */}
            {!selfEdit && mode === 'create' && (
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-muted-foreground/70 uppercase tracking-widest ml-1">Username *</label>
                <input value={formData.username} onChange={e => setFormData({ ...formData, username: e.target.value })}
                  className={cn(crmStyles.input, errors.username && 'border-status-error')} placeholder="ej: juan.perez" />
                {errors.username && <p className="text-xs text-status-error ml-1">{errors.username}</p>}
              </div>
            )}

            {/* Username — read-only en edit */}
            {mode === 'edit' && (
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-muted-foreground/70 uppercase tracking-widest ml-1">Username</label>
                <input value={formData.username} disabled className={cn(crmStyles.input, "opacity-50 cursor-not-allowed")} />
              </div>
            )}

            {/* Rol */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-muted-foreground/70 uppercase tracking-widest ml-1">Rol de Acceso</label>
              {selfEdit ? (
                <input
                  value={{ admin: 'Administrador', profesional: 'Profesional', recepcion: 'Recepción', marketing: 'Marketing' }[formData.rol ?? ''] ?? formData.rol}
                  disabled className={cn(crmStyles.input, "opacity-50 cursor-not-allowed")} />
              ) : (
                <select value={formData.rol} onChange={e => handleRolChange(e.target.value)} className={crmStyles.select}>
                  <option value="recepcion">Recepción / Agenda</option>
                  <option value="profesional">Profesional Médico</option>
                  <option value="marketing">Marketing / Ventas</option>
                  <option value="admin">Administrador</option>
                </select>
              )}
            </div>

            {/* Color */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-muted-foreground/70 uppercase tracking-widest ml-1">Color Identificador</label>
              <div className="flex flex-wrap gap-2.5 p-3 rounded-xl bg-muted border border-border">
                {PRESET_COLORS.map(color => (
                  <button key={color.value} type="button" onClick={() => setFormData({ ...formData, color_agenda: color.value })}
                    className={cn("size-7 rounded-full border-2 transition-all p-0.5 shadow-sm",
                      formData.color_agenda === color.value ? 'border-foreground scale-110' : 'border-transparent hover:scale-110')}>
                    <div className="w-full h-full rounded-full" style={{ backgroundColor: color.value }} />
                  </button>
                ))}
              </div>
            </div>

            {/* Activo — solo admin */}
            {!selfEdit && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/20 border border-border/30">
                <input type="checkbox" id="activo_chk" checked={formData.activo}
                  onChange={e => setFormData({ ...formData, activo: e.target.checked })}
                  className="size-4 rounded border-border text-primary" />
                <label htmlFor="activo_chk" className="text-sm font-semibold text-foreground/80 cursor-pointer select-none">Usuario con acceso activo</label>
              </div>
            )}

            {/* Módulos — solo admin */}
            {!selfEdit && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-muted-foreground/70 uppercase tracking-widest ml-1">Acceso a módulos</label>
                  <button type="button" onClick={() => setModulosCustom(null)}
                    className="text-[10px] text-primary hover:underline">
                    Restaurar defaults del rol
                  </button>
                </div>
                <div className="space-y-3 p-3 rounded-xl bg-muted border border-border">
                  {MODULOS_GRUPOS.map(grupo => (
                    <div key={grupo.label}>
                      <p className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest mb-1.5">{grupo.label}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {grupo.items.map(mod => {
                          const activo = getModulosActivos().includes(mod.key)
                          return (
                            <label key={mod.key} className="flex items-center gap-2 cursor-pointer py-0.5">
                              <input type="checkbox" checked={activo} onChange={() => toggleModulo(mod.key)}
                                className="size-3.5 rounded border-border text-primary" />
                              <span className="text-xs text-foreground/80">{mod.label}</span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                  {modulosCustom !== null && (
                    <p className="text-[10px] text-status-warning">⚠ Acceso personalizado (difiere del rol por defecto)</p>
                  )}
                </div>
              </div>
            )}

            {/* Módulos selfEdit — read-only */}
            {selfEdit && initialData?.modulos_habilitados && (
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-muted-foreground/70 uppercase tracking-widest ml-1">Módulos con acceso</label>
                <div className="flex flex-wrap gap-1.5">
                  {initialData.modulos_habilitados.map(m => (
                    <span key={m} className="text-[10px] px-2 py-0.5 rounded bg-muted border border-border text-muted-foreground font-medium capitalize">{m}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Contraseña — create admin */}
            {!selfEdit && mode === 'create' && (
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-muted-foreground/70 uppercase tracking-widest ml-1">Contraseña *</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    className={cn(crmStyles.input, "pr-10", errors.password && 'border-status-error')}
                    placeholder="Mínimo 8 caracteres" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPassword ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-xs text-status-error ml-1">{errors.password}</p>}
              </div>
            )}

            {/* Cambiar contraseña — selfEdit */}
            {selfEdit && (
              <div className="space-y-3 pt-1 border-t border-border">
                <p className="text-xs font-bold text-muted-foreground/70 uppercase tracking-widest">Cambiar contraseña</p>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground ml-1">Contraseña actual</label>
                  <div className="relative">
                    <input type={showCurrentPass ? "text" : "password"} value={currentPass} onChange={e => setCurrentPass(e.target.value)}
                      autoComplete="current-password" className={cn(crmStyles.input, "pr-10", errors.currentPass && 'border-status-error')} />
                    <button type="button" onClick={() => setShowCurrentPass(!showCurrentPass)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showCurrentPass ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
                    </button>
                  </div>
                  {errors.currentPass && <p className="text-xs text-status-error ml-1">{errors.currentPass}</p>}
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground ml-1">Nueva contraseña</label>
                  <input type="password" value={newPass} onChange={e => setNewPass(e.target.value)}
                    autoComplete="new-password" className={cn(crmStyles.input, errors.newPass && 'border-status-error')} />
                  {errors.newPass && <p className="text-xs text-status-error ml-1">{errors.newPass}</p>}
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-muted-foreground ml-1">Confirmar contraseña</label>
                  <input type="password" value={confirmPass} onChange={e => setConfirmPass(e.target.value)}
                    autoComplete="new-password" className={cn(crmStyles.input, errors.confirmPass && 'border-status-error')} />
                  {errors.confirmPass && <p className="text-xs text-status-error ml-1">{errors.confirmPass}</p>}
                </div>
                <p className="text-[10px] text-muted-foreground/60">Dejá en blanco si no querés cambiar la contraseña.</p>
              </div>
            )}

            {errors.general && (
              <div className="flex items-center gap-2 p-3 text-xs font-medium text-status-error bg-status-error-bg border border-status-error-border rounded-xl">
                <AlertCircle className="size-4 shrink-0" />
                {errors.general}
              </div>
            )}
          </div>
      </ModalForm>

      {fotoSrc && (
        <FotoEditorDialog
          imageSrc={fotoSrc}
          open={editorOpen}
          onOpenChange={setEditorOpen}
          onApply={handleFotoApply}
        />
      )}
    </>
  )
}
